import apiClient from "./apiClient";
import { config } from "./config";

const API_BASE_URL = config.apiBaseUrl;

export interface SmartColumnGenerateRequest {
  table_name: string;
  column_headers: string[];
  rows: any[][];
  user_intent: string;
  total_row_count?: number;
}

export interface ColumnSuggestion {
  column_name: string;
  column_type: "formula" | "computed";
  formula?: string;
  computed_values?: any[];
  computation_logic?: string;
  description: string;
  warnings?: string[];
}

export interface ColumnMetadata {
  column_name: string;
  description: string;
  column_type: "formula" | "computed";
  formula?: string;
  computation_logic?: string;
}

export interface SmartColumnGenerateResponse {
  success: boolean;
  returned_columns?: string[];
  results?: Array<{ [key: string]: any }>;
  column_metadata?: ColumnMetadata[];
  suggestions?: ColumnSuggestion[];
  message: string;
}

export interface ApiError {
  detail: string;
  status: number;
}

class SmartColumnApiService {
  async generateColumn(request: SmartColumnGenerateRequest): Promise<SmartColumnGenerateResponse> {
    try {
      console.log("📤 Sending smart column generation request:", {
        table_name: request.table_name,
        column_count: request.column_headers.length,
        rows_count: request.rows.length,
        user_intent: request.user_intent,
      });

      const response = await apiClient.post<SmartColumnGenerateResponse>(
        '/smart_column/generate',
        request
      );

      const result = response.data;

      // Handle new response structure with column_metadata
      if (result.returned_columns && result.results && result.column_metadata) {
        console.log("✅ Smart column response received (new structure with metadata):", {
          returned_columns_count: result.returned_columns.length,
          results_count: result.results.length,
          metadata_count: result.column_metadata.length,
          returned_columns: result.returned_columns,
          message: result.message,
        });
      }
      // Handle old response structure with suggestions
      else if (result.suggestions) {
        console.log("✅ Smart column suggestions generated successfully:", {
          suggestion_count: result.suggestions.length,
          suggestions: result.suggestions.map(s => s.column_name),
        });
      } else {
        console.warn("⚠️ Unexpected response structure:", result);
      }

      return result;
    } catch (error: any) {
      if (this.isApiError(error)) {
        throw error;
      }

      console.error("❌ Network or unexpected error:", error);

      // Normalise into ApiError shape
      let errorDetail: string;
      if (error.responseData?.detail) {
        const detail = error.responseData.detail;
        errorDetail = Array.isArray(detail) ? detail.join(', ') : String(detail);
      } else {
        errorDetail = error.message || "Network error: Failed to connect to the server";
      }

      throw {
        detail: errorDetail,
        status: error.status || 0,
      } as ApiError;
    }
  }

  async healthCheck(): Promise<{ status: string; service: string; message: string }> {
    try {
      // Health check is unauthenticated — use plain fetch to skip interceptor
      const url = `${API_BASE_URL}/smart_column/health`;
      const response = await fetch(url, { method: "GET" });

      if (!response.ok) {
        throw new Error(`Health check failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("❌ Health check failed:", error);
      throw error;
    }
  }

  private isApiError(error: any): error is ApiError {
    return (
      error &&
      typeof error === "object" &&
      "detail" in error &&
      "status" in error
    );
  }

  static formatErrorMessage(error: ApiError): string {
    if (error.detail && typeof error.detail === 'string') {
      if (error.detail.includes("Please improve your intent") ||
        error.detail.includes("did not pass validation")) {
        return error.detail;
      }
    }

    return error.detail || "An unexpected error occurred. Please try again.";
  }
}

export const smartColumnApiService = new SmartColumnApiService();
export default SmartColumnApiService;
